import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ROUTE_NAMES } from '@app/core/navigation/navigation.constants';
import { PopupService } from '@app/core/services/popup.service';
import { NotificationsAction } from '@app/features/admin/settings/notifications/state/notifications.action';
import { NotificationsState } from '@app/features/admin/settings/notifications/state/notifications.state';
import { Notification, Notifications, ServerNotification } from '@app/features/admin/settings/notifications/state/notifications.types';
import { TranslocoService } from '@ngneat/transloco';
import { InfiniteScrollComponent } from '@shared/components/infinite-scroll/infinite-scroll.component';
import { take } from 'rxjs';

@Component({
  selector: 'notification',
  imports: [MatIcon, MatIconButton, MatTooltip, InfiniteScrollComponent, MatTab, MatTabGroup, MatTabLabel],
  templateUrl: './notification.component.html',
  styles: `
    .notifications-panel {
      .mat-mdc-tab-group .mat-mdc-tab-body-content {
        padding: 0 !important;
      }

      .mat-mdc-tab-group .mat-mdc-tab-header .mat-mdc-tab-label-container {
        margin: 0 !important;
      }

      .mdc-tab {
        padding: 0 32px !important;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationComponent {
  @ViewChild('notificationOrigin') private notificationOrigin: MatButton;
  @ViewChild('notificationPanel') private notificationPanel: TemplateRef<any>;

  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly overlay: Overlay = inject(Overlay);
  protected readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  protected readonly notificationActions: NotificationsAction = inject(NotificationsAction);
  protected readonly notificationState: NotificationsState = inject(NotificationsState);
  protected readonly popupService: PopupService = inject(PopupService);
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly router: Router = inject(Router);
  protected readonly tab: WritableSignal<0 | 1 | 2> = signal(0);
  protected readonly isLoading: WritableSignal<boolean> = signal(false);
  protected readonly notifications: Signal<Notifications> = computed(() => {
    const inbox = this.notificationState.inboxNotifications();
    const archived = this.notificationState.archivedNotifications();
    const notifications = this.notificationState.notifications();

    switch (this.tab()) {
      case 1:
        return inbox;
      case 2:
        return archived;
      default:
        return notifications;
    }
  });
  protected readonly unreadCounts: Signal<number> = computed(() => this.notificationState.notifications()?.unread);
  protected readonly notificationsItems: Signal<Notification[]> = computed(() => this.notifications()?.data || []);
  protected readonly conditionToStopLoading: Signal<boolean> = computed(() => !this.notifications()?.meta?.next_cursor);

  protected overlayRef: OverlayRef;
  protected openPanel(): void {
    if (!this.notificationPanel) {
      return;
    }

    if (!this.overlayRef) {
      this._createOverlay();
    }
    this.tab.set(0);
    this.notificationActions.actions.getNotifications();
    this.overlayRef.attach(new TemplatePortal(this.notificationPanel, this.viewContainerRef));
  }

  protected delete(item: ServerNotification, event?: MouseEvent): void {
    event?.stopPropagation();
    this.popupService.openConfirmationDialog({
      title: this.translocoService.translate('Delete notification'),
      message: this.translocoService.translate('Are you sure you want to delete this notification?'),
      actionType: 'ok',
      iconType: 'warning',
      onCancel: () => null,
      onAccept: () => {
        this.notificationActions.actions.deleteNotification({
          notificationId: item.id
        });
        this.notificationActions.actions.deleteNotificationsSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.notificationState.remove(item);
        });
      }
    });
  }

  protected hide(item: ServerNotification, event?: MouseEvent): void {
    event?.stopPropagation();
    this.popupService.openConfirmationDialog({
      title: this.translocoService.translate('Hide notification'),
      message: this.translocoService.translate('Are you sure you want to hide this notification?'),
      actionType: 'ok',
      iconType: 'warning',
      onCancel: () => null,
      onAccept: () => {
        this.notificationActions.actions.action({
          item,
          action: 'hide'
        });
      }
    });
  }

  protected unhide(item: ServerNotification, event?: MouseEvent): void {
    event?.stopPropagation();
    this.popupService.openConfirmationDialog({
      title: this.translocoService.translate('Unhide notification'),
      message: this.translocoService.translate('Are you sure you want to unhide this notification?'),
      actionType: 'ok',
      iconType: 'warning',
      onCancel: () => null,
      onAccept: () => {
        this.notificationActions.actions.action({
          item,
          action: 'unhide'
        });
      }
    });
  }

  protected read(item: ServerNotification) {
    this.notificationActions.actions.action({
      item,
      action: 'read'
    });
  }

  protected readAll() {
    this.notificationActions.actions.markAllAsRead();
  }

  protected settings() {
    this.router.navigate([`/${ROUTE_NAMES.PAGES}/${ROUTE_NAMES.SETTINGS}/${ROUTE_NAMES.NOTIFICATIONS}`]).then(() => this.overlayRef?.detach());
  }

  protected onScroll() {
    if (this.isLoading()) {
      return;
    }
    this.isLoading.set(true);
    this.notificationActions.actions.getNotifications({
      isLoadMore: true,
      params: {
        after: this.notifications()?.meta?.next_cursor,
        per_page: 20
      }
    });
    this.notificationActions.actions.getNotificationsSuccess$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isLoading.set(false);
    });
  }

  protected onTabChange(selectedIndex: number) {
    this.tab.set(selectedIndex as 0 | 1 | 2);
    switch (selectedIndex) {
      case 1: {
        const notifications = this.notificationState.inboxNotifications();
        if (!notifications) {
          this.notificationActions.actions.getNotifications({
            params: {
              'filter[hide]': 0
            },
            key: 'inboxNotifications'
          });
        }
        break;
      }
      case 2: {
        const notifications = this.notificationState.archivedNotifications();
        if (!notifications) {
          this.notificationActions.actions.getNotifications({
            params: {
              'filter[hide]': 1
            },
            key: 'archivedNotifications'
          });
        }
        break;
      }
    }
  }

  private _createOverlay(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'seal-backdrop-on-mobile',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.notificationOrigin._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom'
          }
        ])
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.overlayRef.detach();
      });
  }
}
